import { Meilisearch } from "meilisearch";
import { createClient } from "../crystallize/client.js";
import { findBooksByAuthorQuery } from "./author.discovery.js";
import { fetchPaginatedQueryDiscovery } from "../crystallize/utils/iterate-paginated-query.js";
import { getContributorData } from "./get-contributor.js";
import { slugify } from "@cappelendamm/utils/slugify";
import { filterNullArrays } from "@cappelendamm/utils/array";
// ============ MEILISEARCH SETTINGS ============
export const AUTHOR_INDEX_NAME = "authors";
export const AUTHOR_INDEX_SETTINGS = {
    searchableAttributes: [
        "objectID",
        "fullName",
        "biography",
        "roles",
        "reviews.reviewer",
        "reviews.source",
    ],
    filterableAttributes: [
        "objectID",
        "roles",
        "skus",
        "reviews.language",
        "indexedAt",
        "lastBookPublicationTimestamp",
        "shops",
        "shops.path",
    ],
    sortableAttributes: ["indexedAt", "lastBookPublicationTimestamp", "lastName"],
    displayedAttributes: ["*"],
};
export async function indexAuthorByScids({ scids }, { crystallize, meilisearch, }) {
    const meilisearchClient = new Meilisearch({
        apiKey: meilisearch.masterKey,
        host: meilisearch.host,
    });
    const index = meilisearchClient.index(AUTHOR_INDEX_NAME);
    const contributorDocuments = await getAuthorIndexDocument({ scids }, crystallize);
    // Separate valid authors from non-authors
    const validAuthors = contributorDocuments.filter((doc) => doc.roles.some((role) => role.toLowerCase() === "forfatter"));
    const validAuthorScids = new Set(validAuthors.map((doc) => doc.scid));
    const scidsToDelete = scids.filter((scid) => !validAuthorScids.has(scid));
    // Batch add valid authors
    if (validAuthors.length > 0) {
        await index.addDocuments(validAuthors, { primaryKey: "objectID" });
    }
    // Batch delete non-authors
    if (scidsToDelete.length > 0) {
        await index.deleteDocuments(scidsToDelete);
    }
}
async function getAuthorIndexDocument({ scids }, config) {
    const contributors = await getContributorData({ scids }, config);
    if (contributors.length === 0)
        return [];
    const documents = [];
    for (const contributor of contributors) {
        // Skip contributors without required fields
        if (!contributor.pimId || !contributor.scid) {
            continue;
        }
        const booksByContributor = await getBooksByContributor({ contributorItemId: contributor.pimId }, config);
        const { roles, skus, reviews, lastBookPublicationTimestamp } = aggregateContributorVariants(booksByContributor);
        documents.push({
            objectID: contributor.objectID,
            pimId: contributor.pimId,
            scid: contributor.scid,
            shops: contributor.shops,
            imageUrl: contributor.imageUrl,
            slug: contributor.slug,
            fullName: contributor.fullName ?? null,
            indexedAt: contributor.indexedAt,
            firstName: contributor.firstName ?? null,
            lastName: contributor.lastName ?? null,
            lastBookPublicationTimestamp,
            roles,
            skus,
            reviews,
        });
    }
    return documents;
}
export function aggregateContributorVariants(booksByContributor) {
    const contributorRoles = new Set();
    const skus = new Set();
    const collectedReview = new Map();
    let lastBookPublicationTimestamp = null;
    for (const variant of booksByContributor) {
        if (variant.sku) {
            skus.add(variant.sku);
        }
        for (const contributor of variant.bidragsyter?.bidragsyter ?? []) {
            const variantRoles = contributor?.rolle;
            if (variantRoles) {
                for (const role of Object.values(variantRoles)) {
                    contributorRoles.add(role);
                }
            }
        }
        const publicationTimestamp = variant.utgivelsesdato
            ? Date.parse(variant.utgivelsesdato)
            : NaN;
        if (!Number.isNaN(publicationTimestamp)) {
            lastBookPublicationTimestamp =
                lastBookPublicationTimestamp === null
                    ? publicationTimestamp
                    : Math.max(lastBookPublicationTimestamp, publicationTimestamp);
        }
        for (const review of variant.anmeldelse ?? []) {
            if (!review)
                continue;
            const encodedReview = encodeReview(review);
            const existingReview = collectedReview.get(encodedReview);
            const reviewSkus = existingReview?.collectedSkus ?? new Set();
            if (variant.sku) {
                reviewSkus.add(variant.sku);
            }
            collectedReview.set(encodedReview, {
                review: {
                    _key: encodedReview,
                    reviewer: review.anmelder,
                    source: review.kilde,
                    text: review.html,
                    language: review.sprak,
                    dice: review.terningkast,
                    link: review.lenke,
                    date: review.dato,
                },
                collectedSkus: reviewSkus,
            });
        }
    }
    return {
        roles: [...contributorRoles],
        skus: [...skus],
        reviews: Array.from(collectedReview.values()).map(({ review, collectedSkus }) => ({
            ...review,
            skus: [...collectedSkus],
        })),
        lastBookPublicationTimestamp,
    };
}
async function getBooksByContributor({ contributorItemId }, config) {
    const crystallizeClient = createClient(config);
    const fetcher = async (after) => {
        const result = await crystallizeClient.discovery.request(findBooksByAuthorQuery, {
            after,
            authorItemId: contributorItemId,
        });
        return result.browse?.bok;
    };
    const products = await fetchPaginatedQueryDiscovery(fetcher);
    return products
        .flatMap((p) => p?.variants)
        .filter((variant) => variant?.bidragsyter?.bidragsyter?.some((contributor) => contributor?.bidragsyter?.items?.some((item) => item?.itemId === contributorItemId)))
        .filter((variant) => !!variant);
}
// ============ HELPER FUNCTIONS ============
/**
 * Created an encoded key from review
 */
export function encodeReview(review) {
    const key = slugify(filterNullArrays([
        review.anmelder,
        review.kilde,
        review.terningkast,
        review.dato,
    ]).map(String));
    return btoa(key);
}
